import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { ExistingProject } from "../../models/project";
import { ExistingCraftRecord } from "../../models/craft-record";
import { DbRead, DbWrite } from "../../database";
import ProjectDetailsPage from "./ProjectDetailsPage";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { ExistingCustomer } from "../../models/customer";
import { ExistingTask } from "../../models/task";
import { ExistingStiltInvoice } from "../../models/invoice";
import { ExistingEstimate } from "../../models/estimate";
import { ExistingAttachment } from "../../models/attachment";
import { useUserPermissionsStore } from "../../store/user-permissions";
import { logger } from "../../logging";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import { useUserDisplayNamesStore } from "../../store/user-display-names-map";
import { generatePDF } from "../../components/Invoices/generatePDF";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { PhotoForGallery } from "./ProjectPhotoGallery";
import { Timestamp } from "firebase/firestore";
import { useAuthStore } from "../../store/firebase-auth";
import EditProjectDialog from "../../components/EditProjectDialog";

export default function ProjectDetailsContainer(props: { siteKey: string }) {
  const { projectID } = useParams<{ projectID: string }>();
  const [project, setProject] = useState<ExistingProject | null>(null);
  const [craftRecords, setCraftRecords] = useState<ExistingCraftRecord[]>([]);
  const [customer, setCustomer] = useState<ExistingCustomer | null>(null);
  const [tasks, setTasks] = useState<ExistingTask[]>([]);
  const [invoices, setInvoices] = useState<ExistingStiltInvoice[]>([]);
  const [estimates, setEstimates] = useState<ExistingEstimate[]>([]);
  const [photos, setPhotos] = useState<PhotoForGallery[]>([]);
  const [attachments, setAttachments] = useState<ExistingAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingCounts, setLoadingCounts] = useState({
    tasks: 0,
    invoices: 0,
    estimates: 0,
    photos: 0,
    attachments: 0,
  });
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [updatingProject, setUpdatingProject] = useState(false);

  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const userPermissions = useUserPermissionsStore(
    (state) => state.siteKeyUserPermissions,
  );
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );
  const [userDisplayNamesMap, userDisplayNamesMapIsLoading] =
    useUserDisplayNamesStore((state) => [
      state.userDisplayNames,
      state.loading,
    ]);

  const siteKeyDoc = useSiteKeyDocStore((state) => state.siteKeyDoc);
  const siteKey = props.siteKey;

  // Handle updating the project
  const handleUpdateProject = async (updatedProjectData: {
    title: string;
    description: string;
  }) => {
    if (!project || !firebaseUser) return;

    try {
      setUpdatingProject(true);

      // Create a partial project object to update
      const projectUpdate = {
        id: project.id,
        refPath: project.refPath,
        title: updatedProjectData.title,
        description: updatedProjectData.description,
        lastModifiedBy: firebaseUser.uid,
        timestampLastModified: Timestamp.now(),
      };

      // Call the database update method
      await DbWrite.projects.update(projectUpdate);

      // Update local state as well
      setProject({
        ...project,
        title: updatedProjectData.title,
        description: updatedProjectData.description,
        lastModifiedBy: firebaseUser.uid,
        timestampLastModified: Timestamp.now(),
      });

      addToastMessage({
        id: createToastMessageID(),
        message: "Project updated successfully",
        dialog: false,
        type: "success",
      });

      setEditProjectDialogOpen(false);
    } catch (error) {
      logger.error("Error updating project:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Failed to update project",
        dialog: false,
        type: "error",
      });
    } finally {
      setUpdatingProject(false);
    }
  };

  // Generate PDF of project details
  const handleGenerateProjectPDF = async () => {
    if (!project || !siteKeyDoc || generatingPDF) return;

    try {
      setGeneratingPDF(true);

      // Use the existing project PDF generator from the generatePDF object
      await generatePDF.project(
        siteKeyDoc,
        project,
        craftRecords,
        customer,
        tasks,
        invoices,
        estimates,
        photos,
        userDisplayNamesMap,
      );

      addToastMessage({
        id: createToastMessageID(),
        message: "Project PDF generated successfully",
        dialog: false,
        type: "success",
      });
    } catch (error) {
      logger.error("Error generating project PDF:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Failed to generate project PDF",
        dialog: false,
        type: "error",
      });
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Computed property for tasks by craft record
  const tasksByCraftRecord = useMemo(() => {
    const groupedTasks: Record<string, ExistingTask[]> = {};
    tasks.forEach((task) => {
      const craftRecordID = task.craftRecordID.split("/").pop() || "";
      if (!groupedTasks[craftRecordID]) {
        groupedTasks[craftRecordID] = [];
      }
      groupedTasks[craftRecordID].push(task);
    });
    return groupedTasks;
  }, [tasks]);

  // Computed property for invoices by craft record
  const invoicesByCraftRecord = useMemo(() => {
    const groupedInvoices: Record<string, ExistingStiltInvoice[]> = {};
    invoices.forEach((invoice) => {
      const craftRecordID = invoice.craftRecordID || "";
      if (!craftRecordID) return;

      if (!groupedInvoices[craftRecordID]) {
        groupedInvoices[craftRecordID] = [];
      }
      groupedInvoices[craftRecordID].push(invoice);
    });
    return groupedInvoices;
  }, [invoices]);

  // Computed property for estimates by craft record
  const estimatesByCraftRecord = useMemo(() => {
    const groupedEstimates: Record<string, ExistingEstimate[]> = {};
    estimates.forEach((estimate) => {
      const craftRecordID = estimate.craftRecordID || "";
      if (!craftRecordID) return;

      if (!groupedEstimates[craftRecordID]) {
        groupedEstimates[craftRecordID] = [];
      }
      groupedEstimates[craftRecordID].push(estimate);
    });
    return groupedEstimates;
  }, [estimates]);

  // Computed property for photos by craft record
  const photosByCraftRecord = useMemo(() => {
    const groupedPhotos: Record<string, PhotoForGallery[]> = {};
    photos.forEach((photo) => {
      const craftRecordID = photo.craftRecordID;

      if (!groupedPhotos[photo.craftRecordID]) {
        groupedPhotos[photo.craftRecordID] = [];
      }

      groupedPhotos[craftRecordID].push(photo);
    });
    return groupedPhotos;
  }, [photos]);

  // Fetch project
  useEffect(() => {
    if (!projectID || !siteKey) return;
    setLoading(true);
    setError(null);

    // Subscribe to project
    const unsubscribeProject = DbRead.projects.subscribe(
      siteKey,
      projectID,
      (proj) => {
        setProject(proj);
      },
      (err) => {
        setError("Failed to load project");
        addToastMessage({
          id: createToastMessageID(),
          message: "Failed to load project",
          dialog: false,
          type: "error",
        });
        logger.error(`Failed to load project: ${err}`);
      },
    );

    return () => {
      unsubscribeProject && unsubscribeProject();
    };
  }, [projectID, siteKey, addToastMessage]);

  // Fetch craftRecords for this project
  useEffect(() => {
    if (!projectID || !siteKey || !project) return;

    // Subscribe to craft records for this project
    const unsubscribeCraftRecords = DbRead.parentRecords.subscribeByProjectID(
      siteKey,
      projectID,
      (records) => {
        setCraftRecords(records);
        if (records.length === 0) {
          setLoading(false);
        }
      },
      (err) => {
        setError("Failed to load jobs for this project");
        addToastMessage({
          id: createToastMessageID(),
          message: "Failed to load jobs for this project",
          dialog: false,
          type: "error",
        });
        logger.error(`Failed to load craft records: ${err}`);
        setLoading(false);
      },
    );

    return () => {
      unsubscribeCraftRecords && unsubscribeCraftRecords();
    };
  }, [projectID, siteKey, project, addToastMessage]);

  // Fetch customer for this project
  useEffect(() => {
    if (craftRecords.length === 0) return;
    if (!siteKey) return;

    const customerIDs = craftRecords.map((record) => record.customerID);
    const uniqueCustomerIDs = [...new Set(customerIDs)].filter(
      Boolean,
    ) as string[];

    if (uniqueCustomerIDs.length === 0) return;

    const customerID = uniqueCustomerIDs[0];

    if (!customerID) return;

    // Fetch customer
    const unsubscribeCustomer = DbRead.customers.subscribe({
      siteKey,
      customerID,
      onChange: (customer) => {
        setCustomer(customer);
      },
      onError: (err) => {
        setError("Failed to load customer");
        logger.error(`Failed to load customer: ${err}`);
      },
    });

    return () => {
      unsubscribeCustomer && unsubscribeCustomer();
    };
  }, [craftRecords, siteKey]);

  // Fetch tasks for all craft records
  useEffect(() => {
    if (!userPermissions || craftRecords.length === 0 || !siteKey) return;

    setLoadingCounts((prev) => ({ ...prev, tasks: craftRecords.length }));

    const allTasks: ExistingTask[] = [];
    const unsubscribers: (() => void)[] = [];

    craftRecords.forEach((craftRecord) => {
      const unsubscribe = DbRead.tasks.subscribeAllByWorkRecordID({
        siteKey,
        craftRecordID: craftRecord.id,
        onChange: (taskList) => {
          allTasks.push(...taskList);
          setTasks((prev) => {
            // Deduplicate tasks based on id
            const taskMap = new Map(prev.map((task) => [task.id, task]));
            taskList.forEach((task) => taskMap.set(task.id, task));
            return Array.from(taskMap.values());
          });
          setLoadingCounts((prev) => ({ ...prev, tasks: prev.tasks - 1 }));
          checkIfDoneLoading();
        },
        onError: (err) => {
          logger.error(`Failed to load tasks for ${craftRecord.id}: ${err}`);
          setLoadingCounts((prev) => ({ ...prev, tasks: prev.tasks - 1 }));
          checkIfDoneLoading();
        },
      });

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [craftRecords, userPermissions, siteKey]);

  // Fetch invoices for all craft records
  useEffect(() => {
    if (craftRecords.length === 0 || !siteKey) return;

    setLoadingCounts((prev) => ({ ...prev, invoices: craftRecords.length }));

    const unsubscribers: (() => void)[] = [];

    craftRecords.forEach((craftRecord) => {
      const unsubscribe = DbRead.invoices.subscribeByCraftRecordId(
        siteKey,
        craftRecord.id,
        (invoiceList) => {
          setInvoices((prev) => {
            // Deduplicate invoices based on id
            const invoiceMap = new Map(
              prev.map((invoice) => [invoice.id, invoice]),
            );
            invoiceList.forEach((invoice) =>
              invoiceMap.set(invoice.id, invoice),
            );
            return Array.from(invoiceMap.values());
          });
          setLoadingCounts((prev) => ({
            ...prev,
            invoices: prev.invoices - 1,
          }));
          checkIfDoneLoading();
        },
        (err) => {
          logger.error(`Failed to load invoices for ${craftRecord.id}: ${err}`);
          setLoadingCounts((prev) => ({
            ...prev,
            invoices: prev.invoices - 1,
          }));
          checkIfDoneLoading();
        },
      );

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [craftRecords, siteKey]);

  // Fetch estimates for all craft records
  useEffect(() => {
    if (craftRecords.length === 0 || !siteKey) return;

    setLoadingCounts((prev) => ({ ...prev, estimates: craftRecords.length }));

    const unsubscribers: (() => void)[] = [];

    craftRecords.forEach((craftRecord) => {
      const unsubscribe = DbRead.estimates.subscribeByCraftRecordId(
        siteKey,
        craftRecord.id,
        (estimateList) => {
          setEstimates((prev) => {
            // Deduplicate estimates based on id
            const estimateMap = new Map(
              prev.map((estimate) => [estimate.id, estimate]),
            );
            estimateList.forEach((estimate) =>
              estimateMap.set(estimate.id, estimate),
            );
            return Array.from(estimateMap.values());
          });
          setLoadingCounts((prev) => ({
            ...prev,
            estimates: prev.estimates - 1,
          }));
          checkIfDoneLoading();
        },
        (err) => {
          logger.error(
            `Failed to load estimates for ${craftRecord.id}: ${err}`,
          );
          setLoadingCounts((prev) => ({
            ...prev,
            estimates: prev.estimates - 1,
          }));
          checkIfDoneLoading();
        },
      );

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [craftRecords, siteKey]);

  // Fetch photos for all craft records
  useEffect(() => {
    if (!userPermissions || craftRecords.length === 0 || !siteKey) return;

    setLoadingCounts((prev) => ({ ...prev, photos: craftRecords.length }));

    const unsubscribers: (() => void)[] = [];

    craftRecords.forEach((craftRecord) => {
      const unsubscribe = DbRead.parentRecordPhotos.subscribeAll({
        siteKey,
        permissions: userPermissions,
        workRecordID: craftRecord.id,
        onChange: (photoList) => {
          setPhotos((prev) => {
            // Deduplicate photos based on id and re-map to PhotoForGallery
            const photoMap = new Map(prev.map((photo) => [photo.id, photo]));
            photoList.forEach((photo) => {
              const photoForGallery: PhotoForGallery = {
                id: photo.id,
                photoURL: photo.photoURL,
                photoURLThumbnail: photo.photoURL_thumb,
                photoURLReduced: photo.photoURL_reduced,
                description: photo.notes ?? "",
                timestampCreated: photo.timestampCreated,
                craftRecordID: craftRecord.id,
              };
              photoMap.set(photo.id, photoForGallery);
            });
            return Array.from(photoMap.values());
          });
          setLoadingCounts((prev) => ({ ...prev, photos: prev.photos - 1 }));
          checkIfDoneLoading();
        },
        onError: (err) => {
          logger.error(`Failed to load photos for ${craftRecord.id}: ${err}`);
          setLoadingCounts((prev) => ({ ...prev, photos: prev.photos - 1 }));
          checkIfDoneLoading();
        },
      });

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [craftRecords, userPermissions, siteKey]);

  // Fetch attachments for all craft records
  useEffect(() => {
    if (!userPermissions || craftRecords.length === 0 || !siteKey) return;

    setLoadingCounts((prev) => ({ ...prev, attachments: craftRecords.length }));

    const unsubscribers: (() => void)[] = [];

    craftRecords.forEach((craftRecord) => {
      const unsubscribe = DbRead.attachments.subscribeAllForWorkRecord({
        siteKey,
        permissions: userPermissions,
        workRecordID: craftRecord.id,
        onChange: (attachmentList) => {
          setAttachments((prev) => {
            // Deduplicate attachments based on id
            const attachmentMap = new Map(
              prev.map((attachment) => [attachment.id, attachment]),
            );
            attachmentList.forEach((attachment) =>
              attachmentMap.set(attachment.id, attachment),
            );
            return Array.from(attachmentMap.values());
          });
          setLoadingCounts((prev) => ({
            ...prev,
            attachments: prev.attachments - 1,
          }));
          checkIfDoneLoading();
        },
        onError: (err) => {
          logger.error(
            `Failed to load attachments for ${craftRecord.id}: ${err}`,
          );
          setLoadingCounts((prev) => ({
            ...prev,
            attachments: prev.attachments - 1,
          }));
          checkIfDoneLoading();
        },
      });

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [craftRecords, userPermissions, siteKey]);

  // Helper to check if all loading is done
  const checkIfDoneLoading = () => {
    const isLoading = Object.values(loadingCounts).some((count) => count > 0);
    if (!isLoading) {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingClipboardAnimation />;
  }
  if (error) {
    return <div className="p-8 text-red-600">{error}</div>;
  }
  if (!project) {
    return <div className="p-8 text-gray-600">Project not found.</div>;
  }

  return (
    <>
      <ProjectDetailsPage
        project={project}
        craftRecords={craftRecords}
        customer={customer}
        tasksByCraftRecord={tasksByCraftRecord}
        invoicesByCraftRecord={invoicesByCraftRecord}
        estimatesByCraftRecord={estimatesByCraftRecord}
        photosByCraftRecord={photosByCraftRecord}
        attachments={attachments}
        userDisplayNamesMap={userDisplayNamesMap}
        isUserDisplayNamesLoading={userDisplayNamesMapIsLoading}
        siteKey={siteKey}
        onGeneratePDF={handleGenerateProjectPDF}
        generatingPDF={generatingPDF}
        onEditProject={() => setEditProjectDialogOpen(true)}
      />

      {/* Edit Project Dialog */}
      {project && (
        <EditProjectDialog
          isOpen={editProjectDialogOpen}
          onClose={() => setEditProjectDialogOpen(false)}
          project={project}
          onSave={handleUpdateProject}
          isUpdating={updatingProject}
        />
      )}
    </>
  );
}
